(() => {
  if (customElements.get('jd1-product-bundle')) return;

  class JD1ProductBundle extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'es-CO', {
        style: 'currency',
        currency: this.dataset.currency || 'COP',
        maximumFractionDigits: 0,
      });

      if (this.querySelector('[data-bundle-item]')) {
        this.initializeBundle();
      } else {
        this.loadRecommendations();
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    async loadRecommendations() {
      const url = this.dataset.recommendationsUrl;
      if (!url || this.dataset.loading === 'true') return;
      this.dataset.loading = 'true';

      try {
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) throw new Error('No fue posible cargar los complementos.');

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const renderedBundle = documentFragment.querySelector(`#${CSS.escape(this.id)}`);
        const content = renderedBundle?.querySelector('.jd1-bundle__content');

        if (!content) {
          this.remove();
          return;
        }

        this.innerHTML = content.outerHTML;
        this.hidden = false;
        this.initializeBundle();
      } catch (error) {
        if (error.name !== 'AbortError') this.remove();
      } finally {
        delete this.dataset.loading;
      }
    }

    initializeBundle() {
      this.currentItem = this.querySelector('[data-current-product]');
      this.submitButton = this.querySelector('[data-bundle-submit]');
      this.message = this.querySelector('[data-bundle-message]');
      this.currentVariants = this.parseCurrentVariants();

      this.addEventListener('change', this.onBundleChange.bind(this), {
        signal: this.abortController.signal,
      });
      this.submitButton?.addEventListener('click', this.addBundle.bind(this), {
        signal: this.abortController.signal,
      });
      document.addEventListener('change', this.onPageVariantChange.bind(this), {
        signal: this.abortController.signal,
      });

      this.updateTotals();
    }

    parseCurrentVariants() {
      const json = this.querySelector('[data-current-variants]')?.textContent;
      if (!json) return [];
      try {
        return JSON.parse(json);
      } catch (_error) {
        return [];
      }
    }

    onBundleChange(event) {
      const variantSelect = event.target.closest('[data-bundle-variant]');
      if (variantSelect) {
        const option = variantSelect.selectedOptions[0];
        const item = variantSelect.closest('[data-bundle-item]');
        item.dataset.variantId = variantSelect.value;
        item.dataset.price = option.dataset.price;
        item.querySelector('[data-item-price]').textContent = this.formatMoney(Number(option.dataset.price));
      }

      if (event.target.matches('[data-bundle-toggle], [data-bundle-variant]')) {
        this.updateTotals();
      }
    }

    onPageVariantChange(event) {
      if (!event.target.matches('[name="id"]')) return;
      if (!event.target.closest(`#shopify-section-${CSS.escape(this.dataset.sectionId)}`)) return;

      const variant = this.currentVariants.find(({ id }) => String(id) === String(event.target.value));
      if (!variant || !this.currentItem) return;

      this.currentItem.dataset.variantId = variant.id;
      this.currentItem.dataset.price = variant.price;
      this.currentItem.querySelector('[data-item-price]').textContent = this.formatMoney(variant.price);
      this.updateTotals();
    }

    selectedItems() {
      return [...this.querySelectorAll('[data-bundle-item]')].filter((item) => {
        const toggle = item.querySelector('[data-bundle-toggle]');
        return !toggle || toggle.checked;
      });
    }

    updateTotals() {
      const items = this.selectedItems();
      const subtotal = items.reduce((sum, item) => sum + Number(item.dataset.price || 0), 0);
      const discount = Number(this.dataset.discount || 0);
      const saving = Math.round(subtotal * (discount / 100));
      const total = subtotal - saving;

      this.querySelector('[data-bundle-subtotal]').textContent = this.formatMoney(subtotal);
      this.querySelector('[data-bundle-total]').textContent = this.formatMoney(total);
      const savingPrefix = this.dataset.savingPrefix || 'Ahorras';
      this.querySelector('[data-bundle-saving]').textContent = `${savingPrefix} ${this.formatMoney(saving)}`;

      if (this.submitButton) this.submitButton.disabled = items.length < 2;
    }

    formatMoney(cents) {
      return this.moneyFormatter.format((Number(cents) || 0) / 100);
    }

    async addBundle() {
      const items = this.selectedItems().map((item) => ({
        id: Number(item.dataset.variantId),
        quantity: 1,
      }));

      if (items.length < 2 || this.submitButton?.disabled) return;

      this.setLoading(true);
      this.showMessage('');

      const sectionIds = (this.dataset.cartSections || '')
        .split(',')
        .map((section) => section.trim())
        .filter(Boolean)
        .slice(0, 5);

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items,
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'No fue posible agregar el paquete.');

        this.renderCartSections(result.sections || {});
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: result, source: 'jd1-bundle' } }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'jd1-bundle' } }));
        this.showMessage(this.dataset.successMessage || 'Paquete agregado al carrito.', 'success');

        requestAnimationFrame(() => {
          const drawer = document.querySelector('cart-drawer');
          if (typeof drawer?.open === 'function') drawer.open();
        });
      } catch (error) {
        this.showMessage(error.message || 'Ocurrió un error. Intenta nuevamente.', 'error');
      } finally {
        this.setLoading(false);
      }
    }

    renderCartSections(sections) {
      Object.entries(sections).forEach(([sectionId, html]) => {
        if (!html) return;
        const currentSection = document.getElementById(`shopify-section-${sectionId}`);
        if (!currentSection) return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const replacement = parsed.getElementById(`shopify-section-${sectionId}`);
        currentSection.innerHTML = replacement ? replacement.innerHTML : html;
      });
    }

    setLoading(loading) {
      if (!this.submitButton) return;
      this.submitButton.classList.toggle('is-loading', loading);
      this.submitButton.disabled = loading;
      this.submitButton.setAttribute('aria-busy', String(loading));
      if (!loading) this.updateTotals();
    }

    showMessage(text, type = '') {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.dataset.type = type;
    }
  }

  customElements.define('jd1-product-bundle', JD1ProductBundle);
})();